/**
 * useSSE.js — Stable SSE connection to /api/sse.
 * Uses a ref for onMessage so the EventSource is NEVER recreated when the
 * handler function changes — only when `enabled` changes.
 */

import { useEffect, useRef } from "react";

export function useSSE(onMessage, enabled = true) {
  // Keep the latest callback in a ref — never triggers a reconnect
  const onMessageRef = useRef(onMessage);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    if (!enabled) return;

    let es;
    let retryDelay = 1000;
    let mounted = true;

    function connect() {
      if (!mounted) return;
      es = new EventSource("/api/sse");

      es.onmessage = (e) => {
        retryDelay = 1000; // reset backoff on success
        try {
          onMessageRef.current(JSON.parse(e.data));
        } catch (_) {}
      };

      es.onerror = () => {
        es.close();
        if (!mounted) return;
        const delay = Math.min(retryDelay, 30_000);
        retryDelay = delay * 1.5;
        setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      mounted = false;
      es?.close();
    };
  }, [enabled]); // ← only reconnect when enabled changes, not on every render
}
